import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import {
  FiUser,
  FiSliders,
  FiBell,
  FiShield,
  FiSettings,
  FiFileText,
  FiArchive,
} from 'react-icons/fi';
import { IoMdTime } from "react-icons/io";
import { GoLaw } from 'react-icons/go';
import { GiExpand, GiContract } from 'react-icons/gi';
import { UserContext } from '../context/UserProvider';

const SettingsShell = (props) => {
  const { role } = useContext(UserContext);
  const [selected, setSelected] = useState('profile');

  const onLinkClick = (sectionId) => {
    scrollToSection(sectionId);
    if (props.toggleFromShell) {
      props.toggleFromShell(sectionId);
    }
    setSelected(sectionId);
  };

  const handleScroll = () => {
    const sections = [
      'profile',
      'preferences',
      'notifications',
      'accounts',
      'archived',
      'resources',
      'vendortypes',
      'feedtypes',
      'filedetails',
      'tos',
      'roster',
    ];
    for (const sectionId of sections) {
      const section = document.getElementById(sectionId);
      if (section) {
        const rect = section.getBoundingClientRect();
        const sectionTop = rect.top;
        const sectionHeight = rect.height;
        const threshold = sectionHeight * 0.5;
        if (sectionTop <= threshold && sectionTop + sectionHeight > threshold) {
          setSelected(sectionId);
          break;
        }
      }
    }
  };

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      const yOffset = -100;
      const y = section.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className="flex min-h-screen bg-gray-100 dark:bg-stone-800">
      <div className="sticky top-0 hidden min-h-screen bg-white dark:bg-stone-700 sm:flex sm:w-3/6 md:w-3/6 lg:w-2/6 xl:w-1/6">
        <div className="ml-8 pr-14 pt-10">
          <nav className="fixed">
            <ul className="space-y-6 text-gray-600 dark:text-gray-200">
              {/* Profile */}
              <li
                className={`w-32 text-lg ${
                  selected === 'profile'
                    ? 'text-blue-600 dark:text-indigo-400'
                    : ''
                }`}
              >
                <Link
                  className="flex select-none items-center"
                  onClick={(e) => {
                    setSelected('profile');
                    scrollToSection('profile');
                    onLinkClick('profile');
                  }}
                >
                  <FiUser className="mr-2"/>
                  Profile
                </Link>
              </li>
              <li
                className={`w-32 text-lg ${
                  selected === 'preferences'
                    ? 'text-blue-600 dark:text-indigo-400'
                    : ''
                }`}
              >
                <Link
                  className="flex select-none items-center"
                  onClick={() => {
                    setSelected('preferences');
                    scrollToSection('preferences');
                    onLinkClick('preferences');
                  }}
                >
                  <FiSliders className="mr-2"/>
                  Preferences
                </Link>
              </li>
              {/* Notifications */}
              <li
                className={`w-32 text-lg ${
                  selected === 'notifications'
                    ? 'text-blue-600 dark:text-indigo-400'
                    : ''
                }`}
              >
                <Link
                  className="flex select-none items-center"
                  onClick={() => {
                    setSelected('notifications');
                    scrollToSection('notifications');
                    onLinkClick('notifications');
                  }}
                >
                  <FiBell className="mr-2"/>
                  Notifications
                </Link>
              </li>
              {/* Admin */}
              <li className="bf w-32 select-none text-lg">
                {role === 'Admin' && (
                  <>
                    <ul className="mt-1 space-y-4">
                      {/* User Accounts */}
                      <li
                        className={`w-40 ${
                          selected === 'accounts'
                            ? 'text-blue-600 dark:text-indigo-400'
                            : ''
                        }`}
                      >
                        <Link
                          className="mt-6 flex items-center"
                          onClick={() => {
                            setSelected('accounts');
                            scrollToSection('accounts');
                            onLinkClick('accounts');
                          }}
                        >
                          <FiUser className="mr-2"/>
                          User Accounts
                        </Link>
                      </li>
                      {/* Archived */}
                      <li
                        className={`w-32 ${
                          selected === 'archived'
                            ? 'text-blue-600 dark:text-indigo-400'
                            : ''
                        }`}
                      >
                        <Link
                          className="mt-6 flex items-center"
                          onClick={() => {
                            setSelected('archived');
                            scrollToSection('archived');
                            onLinkClick('archived');
                          }}
                        >
                          <FiArchive className="mr-2"/>
                          Archived
                        </Link>
                      </li>
                      <li
                        className={`w-32 ${
                          selected === 'resources'
                            ? 'text-blue-600 dark:text-indigo-400'
                            : ''
                        }`}
                      >
                        <Link
                          className="mt-6 flex items-center"
                          onClick={() => {
                            setSelected('resources');
                            scrollToSection('resources');
                            onLinkClick('resources');
                          }}
                        >
                          <FiShield className="mr-2"/>
                          Resources
                        </Link>
                      </li>
                      <li className="w-32">
                        <li
                          className={`w-40 mt-6${
                            selected === 'vendortypes'
                              ? 'font-semibold text-blue-600 dark:text-indigo-400'
                              : ''
                          }`}
                        >
                          <Link
                            className="mt-6 flex cursor-pointer items-center"
                            onClick={() => {
                              setSelected('vendortypes');
                              scrollToSection('vendortypes');
                              onLinkClick('vendortypes');
                            }}
                          >
                            <FiSettings className="mr-2"/>
                            Vendor Types
                          </Link>
                        </li>
                        <li
                          className={`mt-6 w-32 ${
                            selected === 'feedtypes'
                              ? 'text-blue-600 dark:text-indigo-400'
                              : ''
                          }`}
                        >
                          <Link
                            className="flex cursor-pointer items-center"
                            onClick={() => {
                              setSelected('feedtypes');
                              scrollToSection('feedtypes');
                              onLinkClick('feedtypes');
                            }}
                          >
                            <FiSettings className="mr-2"/>
                            Feed Types
                          </Link>
                        </li>
                        <li
                          className={`mt-6 w-32 ${
                            selected === 'filedetails'
                              ? 'text-blue-600 dark:text-indigo-400'
                              : ''
                          }`}
                        >
                          <Link
                            className="flex cursor-pointer items-center"
                            onClick={() => {
                              setSelected('filedetails');
                              scrollToSection('filedetails');
                              onLinkClick('filedetails');
                            }}
                          >
                            <FiFileText className="mr-2"/>
                            File Types
                          </Link>
                        </li>
                        <li
                          className={`mt-6 w-32 ${
                            selected === 'tos'
                              ? 'text-blue-600 dark:text-indigo-400'
                              : ''
                          }`}
                        >
                          <Link
                            className="flex cursor-pointer items-center"
                            onClick={() => {
                              setSelected('tos');
                              scrollToSection('tos');
                              onLinkClick('tos');
                            }}
                          >
                            <GoLaw className="mr-2"/>
                            Terms
                          </Link>
                        </li>
                        <li
                          className={`mt-6 w-32  ${
                            selected === 'roster'
                              ? 'text-blue-600 dark:text-indigo-400'
                              : ''
                          }`}
                        >
                          <Link
                            className="flex cursor-pointer items-center"
                            onClick={() => {
                              setSelected('roster');
                              scrollToSection('roster');
                              onLinkClick('roster');
                            }}
                          >
                            <IoMdTime className="mr-2"/>
                            Roster
                          </Link>
                        </li>
                      </li>
                      {/*<li className="w-32 text-lg select-none" title="under construction">*/}
                      {/*  <div*/}
                      {/*    className="flex mt-6 items-center cursor-pointer"*/}
                      {/*  >*/}
                      {/*    <FaRegMoneyBillAlt className="mr-2"/>*/}
                      {/*    <p>Billing</p>*/}
                      {/*  </div>*/}
                      {/*</li>*/}
                    </ul>
                  </>
                )}
              </li>
            </ul>
          </nav>
        </div>
      </div>
      <div className="w-full sm:w-full">
        <div className="fixed bottom-6 right-0 flex justify-end p-4">
          <button
            className="text-lg text-indigo-600 dark:text-gray-200"
            onClick={props.toggleAllSections}
          >
            {props.isAllExpanded ? (
              <>
                Collapse All
                <GiContract className="mb-1 ml-2 inline-block h-5 w-5"/>
              </>
            ) : (
              <>
                Expand All
                <GiExpand className="h-4.5 w-4.5 mb-1 ml-2 inline-block"/>
              </>
            )}
          </button>
        </div>
        {props.children}
      </div>
    </div>
  );
};

export default SettingsShell;